






































import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {debounce, isString} from 'lodash';
import {SettingConstants} from '@/services/settings.service';
import BindAttrs from '@/utils/binds-attrs';
import AmetrasSimpleTextOutputTemplate from '@/fields/AmetrasSimpleTextOutputTemplate.vue';
import {FieldStyleContext} from '@/fields/field-style-context';

/**
 * General Component for Fields of type NUMBER
 *
 * https://confluence.ametras.com/x/L4RQAw
 */
@Component({
  components: {AmetrasSimpleTextOutputTemplate},
  mixins: [BindAttrs]
})
export default class AmetrasNumberFieldTemplate extends Vue {

  private static readonly MAX_CONSTRAINT_KEY = 'max';
  private static readonly MIN_CONSTRAINT_KEY = 'min';
  private static readonly PLACES_KEY = 'places';
  private static readonly START_KEY = 'start';
  private static readonly NUMBER_FORMAT = 'format';

  @Prop() textAlign!: 'LEFT' | 'RIGHT' | 'CENTER' | 'JUSTIFIED';
  @Prop() marginDefinition!: string;
  @Prop() paddingDefinition!: string;
  @Prop({
    default: null
  }) value!: number | null; // might be undefined
  @Prop() debounce!: boolean;
  @Prop() readonly!: boolean;
  @Prop() allowZero!: boolean; // 0 input is allowed
  @Prop() min!: number;
  @Prop() max!: number;
  @Prop() places!: number;
  @Prop({
    default: false
  }) required!: boolean;
  /**
   * Indicates that we should render a simple mode of this component (increases the rendering performance)
   */
  @Prop() simple!: boolean;
  /**
   * any external error present
   */
  @Prop() error!: boolean;
  /**
   * Indicates that all errors should be shown
   */
  @Prop() forceShowErrors!: boolean;

  @Prop() typeSpecificSettings!: DisguiseDto.NumberFieldSpecificSettings;

  @Prop() styleContext!: FieldStyleContext;

  listeners: Record<string, any> = {};
  internalValue: string = '';
  errors: Map<string, string> = new Map<string, string>();
  firstErrorMsg: string = '';

  rules = [(currentValue: string) => {
    if (this.required && (this.forceShowErrors || !this.readonly) && (!currentValue)) {
      return 'Bitte geben Sie einen Wert an!';
    }
    const notHasAnyExternalError = typeof this.error === 'boolean' ? !this.error : true;
    const notHasAnyInternalError = this.errors.size === 0;
    return notHasAnyExternalError && notHasAnyInternalError; // if any error from outside or any local error, return false
  }];

  $refs!: {
    inputfield: Vue
  };

  private originalInputListeners: any[] = [];
  private originalChangeListeners: any[] = [];

  private debounceFunction = debounce((newValue: string) => {
    this.applyModelValueIfValid(newValue, (valueToBePublished) => {
      this.lastKnownExternalValue = valueToBePublished;
      // update model only if valid.
      for (const fnc of this.originalInputListeners) {
        fnc(valueToBePublished);
      }
    });
  }, SettingConstants.DEBOUNCE_INPUT_MS);
  private API_SEPARATOR_SIGN = '.';
  private USER_SEPARATOR_SIGN = ',';
  private USER_THOUSAND_SEPARATOR_SIGN = '.';
  // holds the last external value to prevent loops (emitting of input/change event -> modelChanges)
  private lastKnownExternalValue: number | null = null;


  created() {
    this.lastKnownExternalValue = this.value;
    this.applyUserReadableFormat(this.value);
    if (this.$listeners.input) {
      if (Array.isArray(this.$listeners.input)) {
        this.originalInputListeners = this.originalInputListeners.concat(this.$listeners.input);
      } else {
        this.originalInputListeners.push(this.$listeners.input);
      }
    }
    if (this.$listeners.change) {
      if (Array.isArray(this.$listeners.change)) {
        this.originalChangeListeners = this.originalChangeListeners.concat(this.$listeners.change);
      } else {
        this.originalChangeListeners.push(this.$listeners.change);
      }
    }
    this.listeners = {
      ...this.$listeners,
      input: (newValue: string) => {
        if (this.debounce) {
          this.debounceFunction(newValue);
        } else {
          this.applyModelValueIfValid(newValue, (valueToBePublished) => {
            this.lastKnownExternalValue = valueToBePublished;
            // update model only if valid.
            for (const fnc of this.originalInputListeners) {
              fnc(valueToBePublished);
            }
          });
        }
      },
      change: (newValue: string) => {
        this.applyModelValueIfValid(newValue, (valueToBePublished) => {
          this.lastKnownExternalValue = valueToBePublished;
          // update model only if valid.
          for (const fnc of this.originalChangeListeners) {
            fnc(valueToBePublished);
            Vue.nextTick(() => {
              this.applyUserReadableFormat(valueToBePublished);
            });
          }
        });
      }
    };
  }

  @Watch('value') modelChanges(newValue: number | null) {
    if (newValue !== this.lastKnownExternalValue) {
      this.lastKnownExternalValue = this.value;
      this.applyUserReadableFormat(newValue);
      this.refreshValidation();
    }
  }

  @Watch('required') requiredChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      if (newValue === false) {
        this.refreshValidation();
      }
    }
  }

  @Watch('min') minChanges(newValue: number, oldValue: number) {
    if (newValue !== oldValue) {
      // trigger validation with current value
      this.applyModelValueIfValid(this.internalValue, () => undefined);
    }
  }

  @Watch('max') maxChanges(newValue: number, oldValue: number) {
    if (newValue !== oldValue) {
      // trigger validation with current value
      this.applyModelValueIfValid(this.internalValue, () => undefined);
    }
  }

  @Watch('places') placesChanges(newValue: number, oldValue: number) {
    if (newValue !== oldValue) {
      // trigger validation with current value
      this.applyModelValueIfValid(this.internalValue, () => undefined);
    }
  }

  @Watch('error') errorChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.refreshValidation();
    }
  }

  /**
   * Formats the given value to a user readable version
   * @param value
   */
  applyUserReadableFormat(value: any) {
    if (!isNaN(value)) {
      let emptyPlacesFillCount;
      if (this.typeSpecificSettings
          && this.typeSpecificSettings.emptyPlacesFillCount !== undefined
          && this.typeSpecificSettings.emptyPlacesFillCount <= this.places
      ) {
        emptyPlacesFillCount = this.typeSpecificSettings.emptyPlacesFillCount;
      } else {
        emptyPlacesFillCount = this.places > 2 ? 2 : this.places;
      }

      const rightZeroTrimmedValue = String(Number(value));
      const afterDecimalPoint = rightZeroTrimmedValue.split(this.API_SEPARATOR_SIGN)[1] || '';
      let numberWithFilledOrOriginalPlaces;
      if (afterDecimalPoint.length < emptyPlacesFillCount || Number(afterDecimalPoint) === 0) {
        // the after decimal point length is less than the places to be filled.
        numberWithFilledOrOriginalPlaces = Number(value).toFixed(emptyPlacesFillCount);
      } else {
        // we have more places than to be filled
        numberWithFilledOrOriginalPlaces = rightZeroTrimmedValue;
      }

      let result: string;
      if (Number(numberWithFilledOrOriginalPlaces) === 0 && (!this.allowZero || value == null || value === '')) {
        // is a number and number equals to 0 or 0.0000000
        result = '';
      } else {
        result = numberWithFilledOrOriginalPlaces.replace(this.API_SEPARATOR_SIGN, this.USER_SEPARATOR_SIGN);
      }

      if (this.typeSpecificSettings && this.typeSpecificSettings.showThousandsSeparator === true) {
        try {
          const thousandSeparatorPattern = new RegExp('\\B(?<!\\,\\d*)(?=(\\d{3})+(?!\\d))', 'g');
          this.internalValue = result.replace(thousandSeparatorPattern, this.USER_THOUSAND_SEPARATOR_SIGN);
        } catch (error) {
          this.internalValue = result;
        }
      } else {
        this.internalValue = result;
      }
    }
  }

  /* Validators */
  validateNumber(evt: KeyboardEvent | undefined) {
    const key: string | undefined = evt?.key;

    const doesMinusExist = this.checkIfMinusKeyExists();
    // Min has to be negative in order to add a minus
    if (key === '-' && !doesMinusExist && this.min < 0) {
      return true;
    }

    if (evt instanceof KeyboardEvent) {
      const charCode = evt.which ? evt.which : evt.keyCode;
      const keyCodeOutOfRange = charCode > 31 &&
          (charCode < 48 || charCode > 57) &&
          charCode !== 44;
      if (keyCodeOutOfRange) {
        evt.preventDefault();
      } else {
        return true;
      }
    }
  }

  /**
   * Checks if the minus symbol already exists in the current input value
   * return true if "minus" exists
   * @private
   */
  private checkIfMinusKeyExists() {

    const doesMinusExist = this.internalValue.includes('-');

    return doesMinusExist;
  }

  private convertInternalValueToExternalValue(value: string) {
    let result = null;
    if (value !== undefined && ((value + '').trim() !== '')) {
      let optionalReplacedThousandsValue;
      if (this.typeSpecificSettings && this.typeSpecificSettings.showThousandsSeparator === true) {
        optionalReplacedThousandsValue = value.replaceAll(this.USER_THOUSAND_SEPARATOR_SIGN, ''); // replace thousand separator
      } else {
        optionalReplacedThousandsValue = value;
      }

      result = Number(optionalReplacedThousandsValue.replace(this.USER_SEPARATOR_SIGN, this.API_SEPARATOR_SIGN)); // replace decimal point
    }
    return result;
  }

  private applyModelValueIfValid(newValue: string, validCb: (valueToBePublished: number | null) => void) {
    let valid: boolean = true;
    valid = valid && this.validatePlaces(newValue);
    valid = valid && this.validateStart(newValue);
    const convertedValue = this.convertInternalValueToExternalValue(newValue);
    valid = valid && this.validateNumberFormat(convertedValue);
    valid = valid && this.validateMinConstraint(convertedValue);
    valid = valid && this.validateMaxConstraint(convertedValue);

    if (!valid) {
      this.firstErrorMsg = this.errors.values().next().value;
    } else {
      this.firstErrorMsg = '';
    }
    if (valid) {
      validCb(convertedValue);
    }
  }

  private validateNumberFormat(newValue: number | null) {
    if (newValue != null) {
      const valid = !isNaN(newValue);
      if (!valid) {
        this.errors.set(AmetrasNumberFieldTemplate.NUMBER_FORMAT, 'Ist keine gültige Eingabe');
      } else {
        this.errors.delete(AmetrasNumberFieldTemplate.NUMBER_FORMAT);
      }
      return valid;
    } else {
      // if null, then the format matches always
      this.errors.delete(AmetrasNumberFieldTemplate.NUMBER_FORMAT);
      return true;
    }
  }

  private validateMinConstraint(newValue: number | null) {
    if (newValue != null) {
      const valid = !this.min || newValue >= this.min;

      if (!valid) {

        this.errors.set(AmetrasNumberFieldTemplate.MIN_CONSTRAINT_KEY,
            'Muss größer gleich ' + this.min + ' sein');
      } else {
        this.errors.delete(AmetrasNumberFieldTemplate.MIN_CONSTRAINT_KEY);
      }
      return valid;
    } else {
      // if null, then the min constraint matches always
      this.errors.delete(AmetrasNumberFieldTemplate.MIN_CONSTRAINT_KEY);
      return true;
    }
  }

  private validateMaxConstraint(newValue: number | null) {
    if (newValue != null) {
      const valid = !this.max || newValue <= this.max;
      if (!valid) {
        this.errors.set(AmetrasNumberFieldTemplate.MAX_CONSTRAINT_KEY, 'Muss kleiner gleich ' + this.max + ' sein');
      } else {
        this.errors.delete(AmetrasNumberFieldTemplate.MAX_CONSTRAINT_KEY);
      }
      return valid;
    } else {
      // if null, then the max constraint matches always
      this.errors.delete(AmetrasNumberFieldTemplate.MAX_CONSTRAINT_KEY);
      return true;
    }
  }

  private validatePlaces(newValue: string) {
    const afterDecimalPoint = newValue.split(this.USER_SEPARATOR_SIGN)[1];
    const valid = !(afterDecimalPoint && afterDecimalPoint.length > this.places);
    if (!valid) {
      this.errors.set(AmetrasNumberFieldTemplate.PLACES_KEY,
          'Muss weniger Nachkommastellen als ' + this.places + ' haben');
    } else {
      this.errors.delete(AmetrasNumberFieldTemplate.PLACES_KEY);
    }
    return valid;
  }

  private validateStart(newValue: string) {
    if (newValue === '') {
      return true;
    }
    const beforeDecimalPoint = newValue.split(this.USER_SEPARATOR_SIGN)[0];
    const valid = isString(beforeDecimalPoint) && beforeDecimalPoint.length >= 1;
    if (!valid) {
      this.errors.set(AmetrasNumberFieldTemplate.START_KEY, 'Muss mit einer Ziffer beginnen');
    } else {
      this.errors.delete(AmetrasNumberFieldTemplate.START_KEY);
    }
    return valid;

  }

  private refreshValidation() {
    if (!this.simple) {
      const inputFieldRef = this.$refs.inputfield as any;
      if (inputFieldRef !== undefined) {
        inputFieldRef.validate();
      } else {
        throw new Error('Cannot refresh validation, the input field is not available');
      }
    }
  }
}
